import { useMemo } from 'react'
import { groupBy, mapValues, values } from 'remeda'
import { z } from 'zod'
import { useGetAuthToken } from '~/features/storyous/hooks/useGetAuthToken'
import { useGetStockList } from '~/features/storyous/hooks/useGetStockList'
import { useGetStoryousMenu } from '~/features/storyous/hooks/useGetStoryousMenu'
import { getZodErrorMessage } from '../../../../shared/utils'

const schema = z.object({
  name: z.string(),
  price: z.number(),
  store: z.enum(['mandarin', 'wollem']),
  amount: z.number(),
})

export type StoryousItem = z.TypeOf<typeof schema>
export type InvalidStoryousItem = {
  name: string
  store: 'mandarin' | 'wollem'
  message: string | null
}

export const useStoryous = ({ merchants }: { merchants: Array<'wollem' | 'mandarin'> }) => {
  const { data: auth, isLoading: isAuthLoading } = useGetAuthToken()

  const menuQueries = useGetStoryousMenu({
    token: auth?.access_token,
    merchants,
  })
  const stockListQueries = useGetStockList({
    token: auth?.access_token,
    merchants,
  })

  const isMenuLoading = menuQueries.some((query) => query.isLoading)
  const isStockLoading = stockListQueries.some((query) => query.isLoading)

  const menuData = useMemo(
    () => menuQueries.flatMap((query) => (query.data ? [query.data] : [])).flat() ?? [],
    [menuQueries]
  )
  const stockData = useMemo(
    () => stockListQueries.flatMap((query) => (query.data ? [query.data] : [])).flat() ?? [],
    [stockListQueries]
  )

  const models = useMemo(() => {
    const allItems = [...menuData, ...stockData]

    const group = groupBy(allItems, (m) => `${m.store}-${m.name}`)

    /**
     * [
        {
          "name": "DIBR12100",
          "price": 131190,
          "store": "mandarin"
        },
        {
          "name": "DIBR12100",
          "amount": 1,
          "store": "mandarin"
        }
      ] 
     */

    const merged = mapValues(group, (array) => array.reduce((acc, el) => ({ ...acc, ...el })))

    return values(merged).reduce<{
      valid: StoryousItem[]
      invalid: InvalidStoryousItem[]
    }>(
      (acc, item) => {
        const parsed = schema.safeParse(item)
        if (parsed.success) {
          acc.valid.push(parsed.data)
        } else {
          const message = getZodErrorMessage(parsed.error)
          acc.invalid.push({ ...item, message })
        }

        return acc
      },
      { valid: [], invalid: [] }
    )
  }, [menuData, stockData])

  return {
    data: models,
    isLoading: isAuthLoading || isMenuLoading || isStockLoading,
    // This is intentional for debugging purposes
    menuData,
    stockData,
  }
}
